import React from 'react';

/**
 * THE REAL BRAND FILES.
 *
 * Both of these are the exact assets the site header uses (see Layout.tsx) —
 * not a lookalike and not typeset. An earlier version of this file set the
 * wordmark in Baloo 2 beside the mark, which is close enough to pass on a
 * page and wrong the moment anyone puts it next to the real one.
 *
 * Vector.png is the mark; giga4x.png is the wordmark. On dark footage both are
 * flipped with the same brightness/invert filter the header uses on its dark
 * plates, so the reversed pair is derived from the originals rather than being
 * a second set of files that can drift.
 */
const GIGA_MARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png';
const GIGA_WORDMARK = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png';

/** The header's own treatment for placing the brand on something dark. */
const REVERSE = 'brightness(0) invert(1)';

/**
 * BRAND MARKS FOR VIDEO.
 *
 * A creator needs a mark far more often than a screen: a corner bug on the
 * clip, a logo beside a tool's logo when saying "Giga reads your Salesforce",
 * a thumbnail. The screen library had lockups (mark + word in a capsule) but
 * never the bare marks, so anyone wanting just the Giga glyph was cropping it
 * out of a screenshot.
 *
 * These export on a genuinely transparent artboard at 4× — a mark is placed
 * small and scaled by whoever uses it, so it needs the headroom the wide
 * frames do not.
 */

export const GIGA_ORANGE = '#EA580C';

/** Giga's own marks, in the forms a video actually asks for. */
export type BrandAsset = {
  id: string;
  name: string;
  note: string;
  /** Rendered size on the artboard, before the 4× export. */
  boxW: number;
  boxH: number;
  /** Marks on a dark plate need the checkerboard behind them inverted. */
  onDark?: boolean;
  render: () => React.ReactNode;
};
function Lockup({
  reversed
}: {
  reversed?: boolean;
}) {
  return <span className="inline-flex items-center gap-2.5" style={{
    filter: reversed ? REVERSE : undefined
  }} data-id="element-3230" data-mp="u7v9qy8zgohz">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className="h-[38px] w-[38px] object-contain" data-id="element-3231" data-mp="fjs2zmn498s4" />
      <img src={GIGA_WORDMARK} alt="Giga" className="h-[32px] object-contain" data-id="element-3232" data-mp="khthrvdielxs" />
    </span>;
}
export const BRAND_ASSETS: BrandAsset[] = [{
  id: 'brand-mark',
  name: 'Giga mark',
  note: 'Glyph only, transparent',
  boxW: 120,
  boxH: 120,
  render: () => <img src={GIGA_MARK} alt="Giga" className="size-[92px] object-contain" data-id="element-3233" data-mp="bfkarfhg9ap5" />
}, {
  id: 'brand-mark-white',
  name: 'Giga mark, reversed',
  note: 'The same glyph, for dark footage',
  boxW: 120,
  boxH: 120,
  onDark: true,
  render: () => <img src={GIGA_MARK} alt="Giga" className="size-[92px] object-contain" style={{
    filter: REVERSE
  }} data-id="element-3234" data-mp="1qyni19podc97" />
}, {
  id: 'brand-wordmark',
  name: 'Lockup',
  note: 'Mark and wordmark, for light backgrounds',
  boxW: 260,
  boxH: 110,
  render: () => <Lockup data-id="element-3235" data-mp="3gg2f2691ho7k" />
}, {
  id: 'brand-wordmark-white',
  name: 'Lockup, reversed',
  note: 'For dark footage and overlays',
  boxW: 260,
  boxH: 110,
  onDark: true,
  render: () => <Lockup reversed data-id="element-3236" data-mp="3q6dy701ao3z1" />
}, {
  id: 'brand-bug',
  name: 'Corner bug',
  note: 'Lockup on an ink chip — sits on any footage',
  boxW: 200,
  boxH: 110,
  render: () => <span className="inline-flex items-center gap-2 rounded-full bg-[#2B211A] px-3.5 py-2.5" data-id="element-3237" data-mp="30mo1pq9lale">
      <img src={GIGA_MARK} alt="" aria-hidden="true" className="size-[18px] object-contain" style={{
      filter: REVERSE
    }} data-id="element-3238" data-mp="32h7ezlw0und" />
      <img src={GIGA_WORDMARK} alt="Giga" className="h-[15px] object-contain" style={{
      filter: REVERSE
    }} data-id="element-3239" data-mp="cz2f5spakjuw" />
    </span>
}];

/**
 * The closer's ground — the warm near-black the rendered MP4s resolve on.
 * Sampled from the closer's final frame so the still card and the video are
 * literally the same colour; anything close-but-different reads as a mistake
 * when they are cut back to back.
 */
export const CLOSER_GROUND = '#3A322C';

/**
 * THE END CARD — the closer's last frame, frozen.
 *
 * Deliberately identical to where the rendered MP4 lands: the address in the
 * rounded wordmark face, white, centred, on the warm near-black ground, and
 * nothing else. An earlier version stacked a mark, the name and a strapline
 * with the address in a pill — a nicer composition, and wrong, because a clip
 * that ends on the video and one that ends on this card would have looked like
 * they came from two different companies.
 *
 * It carries type — the exception to the rule that these plates stay
 * captionless — because an end card IS the caption, and the words never
 * change.
 */
export function EndCard({
  ratio = '9:16'
}: {
  ratio?: '9:16' | '16:9';
}) {
  return <div className="flex h-full w-full items-center justify-center" style={{
    backgroundColor: CLOSER_GROUND
  }} data-id="element-3240" data-mp="19riprfxi37i5">
      <p className="font-logo leading-none tracking-[-0.01em] text-white" style={{
      fontSize: ratio === '9:16' ? 46 : 64
    }} data-id="element-3241" data-mp="1epk81yn3r2sk">
        getgiga.com
      </p>
    </div>;
}

/*
 * A LOWER THIRD used to live here — a name strip for whoever is on camera.
 * Removed rather than kept: this page is for affiliates and creators, and a
 * strip carrying a fixed Giga employee's name and title is not an asset any of
 * them can use. If an internal, editable version is ever wanted, it belongs on
 * an internal tool, not in a public download page.
 */